import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { FileText, TrendingUp, TrendingDown, DollarSign, Download } from 'lucide-react';
import { apiClient } from '../../utils/apiHelper';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IncomeExpensesReport = () => {
  const [report, setReport] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAllIncome, setShowAllIncome] = useState(true);
  const [showAllExpense, setShowAllExpense] = useState(true);

  useEffect(() => { fetchReport(); }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const response = await fetch(`${API_URL}/api/accounts/income-expense-report?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.status === 200 || response.data) {
        const data = await response.json();
        setReport(data);
      } else {
        const errorText = await response.text();
        console.error('Error fetching report:', response.status, errorText);
        alert(`Failed to load report: ${response.status} ${errorText}`);
      }
    } catch (error) {
      console.error('Error:', error);
      alert(`Error loading report: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Group by category
  const getIncomeByCategory = () => {
    if (!report?.incomes) return {};
    const grouped = {};
    report.incomes.forEach(inc => {
      const cat = inc.category || 'Other';
      if (!grouped[cat]) grouped[cat] = { total: 0, count: 0 };
      grouped[cat].total += inc.amount || 0;
      grouped[cat].count++;
    });
    return grouped;
  };

  const getExpenseByCategory = () => {
    if (!report?.expenses) return {};
    const grouped = {};
    report.expenses.forEach(exp => {
      const cat = exp.category || 'Other';
      if (!grouped[cat]) grouped[cat] = { total: 0, count: 0 };
      grouped[cat].total += exp.amount || 0;
      grouped[cat].count++;
    });
    return grouped;
  };

  const incomeByCategory = getIncomeByCategory();
  const expenseByCategory = getExpenseByCategory();

  const displayedIncomes = showAllIncome ? (report?.incomes || []) : (report?.incomes || []).slice(0, 10);
  const displayedExpenses = showAllExpense ? (report?.expenses || []) : (report?.expenses || []).slice(0, 10);

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            Income & Expenses Report
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Date Filters */}
          <div className="flex gap-4 items-end mb-6">
            <div className="flex-1">
              <Label>Start Date</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="flex-1">
              <Label>End Date</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
            <Button onClick={fetchReport} disabled={loading}>
              <FileText className="w-4 h-4 mr-2" />
              {loading ? 'Loading...' : 'Generate Report'}
            </Button>
          </div>

          {report && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-green-600">Total Income</p>
                        <p className="text-2xl font-bold text-green-700">₹{report.summary.total_income.toLocaleString('en-IN')}</p>
                        <p className="text-xs text-green-600">{report.incomes?.length || 0} transactions</p>
                      </div>
                      <TrendingUp className="w-8 h-8 text-green-500 opacity-50" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-red-50 border-red-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-red-600">Total Expenses</p>
                        <p className="text-2xl font-bold text-red-700">₹{report.summary.total_expense.toLocaleString('en-IN')}</p>
                        <p className="text-xs text-red-600">{report.expenses?.length || 0} transactions</p>
                      </div>
                      <TrendingDown className="w-8 h-8 text-red-500 opacity-50" />
                    </div>
                  </CardContent>
                </Card>
                <Card className={`${report.summary.net_profit >= 0 ? 'bg-blue-50 border-blue-200' : 'bg-orange-50 border-orange-200'}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className={`text-sm ${report.summary.net_profit >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                          Net {report.summary.net_profit >= 0 ? 'Profit' : 'Loss'}
                        </p>
                        <p className={`text-2xl font-bold ${report.summary.net_profit >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>
                          ₹{Math.abs(report.summary.net_profit).toLocaleString('en-IN')}
                        </p>
                        <p className={`text-xs ${report.summary.net_profit >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                          {report.summary.net_profit >= 0 ? '▲ Profit' : '▼ Loss'}
                        </p>
                      </div>
                      <DollarSign className={`w-8 h-8 ${report.summary.net_profit >= 0 ? 'text-blue-500' : 'text-orange-500'} opacity-50`} />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Category-wise Summary */}
              <div className="grid grid-cols-2 gap-6 mb-6">
                {/* Income by Category */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base text-green-700">Income by Category</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {Object.entries(incomeByCategory).map(([cat, data]) => (
                        <div key={cat} className="flex justify-between items-center p-2 bg-green-50 rounded">
                          <span className="text-sm font-medium">{cat}</span>
                          <div className="text-right">
                            <span className="text-sm font-bold text-green-600">₹{data.total.toLocaleString('en-IN')}</span>
                            <span className="text-xs text-gray-500 ml-2">({data.count})</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Expense by Category */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base text-red-700">Expense by Category</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {Object.entries(expenseByCategory).map(([cat, data]) => (
                        <div key={cat} className="flex justify-between items-center p-2 bg-red-50 rounded">
                          <span className="text-sm font-medium">{cat}</span>
                          <div className="text-right">
                            <span className="text-sm font-bold text-red-600">₹{data.total.toLocaleString('en-IN')}</span>
                            <span className="text-xs text-gray-500 ml-2">({data.count})</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Detailed Tables */}
              <div className="grid grid-cols-2 gap-6">
                {/* Income Table */}
                <Card>
                  <CardHeader className="pb-2 flex flex-row items-center justify-between">
                    <CardTitle className="text-base">All Income ({report.incomes?.length || 0})</CardTitle>
                    {report.incomes?.length > 10 && (
                      <Button variant="outline" size="sm" onClick={() => setShowAllIncome(!showAllIncome)}>
                        {showAllIncome ? 'Show Less' : `Show All (${report.incomes.length})`}
                      </Button>
                    )}
                  </CardHeader>
                  <CardContent>
                    <div className="max-h-[400px] overflow-y-auto">
                      <table className="w-full border-collapse text-sm">
                        <thead className="sticky top-0 bg-white">
                          <tr className="border-b">
                            <th className="text-left p-2">Date</th>
                            <th className="text-left p-2">Category</th>
                            <th className="text-left p-2">Mode</th>
                            <th className="text-right p-2">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {displayedIncomes.map((income, idx) => (
                            <tr key={idx} className="border-b hover:bg-gray-50">
                              <td className="p-2">{income.income_date}</td>
                              <td className="p-2">{income.category}</td>
                              <td className="p-2">
                                <span className={`px-1.5 py-0.5 text-xs rounded ${
                                  income.payment_mode === 'Cash' ? 'bg-green-100 text-green-800' :
                                  income.payment_mode === 'Bank' ? 'bg-blue-100 text-blue-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {income.payment_mode || 'N/A'}
                                </span>
                              </td>
                              <td className="p-2 text-right text-green-600 font-medium">₹{income.amount?.toLocaleString('en-IN')}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="sticky bottom-0 bg-green-50">
                          <tr className="font-bold">
                            <td colSpan="3" className="p-2 text-right">Total:</td>
                            <td className="p-2 text-right text-green-700">₹{report.summary.total_income.toLocaleString('en-IN')}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </CardContent>
                </Card>

                {/* Expense Table */}
                <Card>
                  <CardHeader className="pb-2 flex flex-row items-center justify-between">
                    <CardTitle className="text-base">All Expenses ({report.expenses?.length || 0})</CardTitle>
                    {report.expenses?.length > 10 && (
                      <Button variant="outline" size="sm" onClick={() => setShowAllExpense(!showAllExpense)}>
                        {showAllExpense ? 'Show Less' : `Show All (${report.expenses.length})`}
                      </Button>
                    )}
                  </CardHeader>
                  <CardContent>
                    <div className="max-h-[400px] overflow-y-auto">
                      <table className="w-full border-collapse text-sm">
                        <thead className="sticky top-0 bg-white">
                          <tr className="border-b">
                            <th className="text-left p-2">Date</th>
                            <th className="text-left p-2">Category</th>
                            <th className="text-left p-2">Mode</th>
                            <th className="text-right p-2">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {displayedExpenses.map((expense, idx) => (
                            <tr key={idx} className="border-b hover:bg-gray-50">
                              <td className="p-2">{expense.expense_date}</td>
                              <td className="p-2">{expense.category}</td>
                              <td className="p-2">
                                <span className={`px-1.5 py-0.5 text-xs rounded ${
                                  expense.payment_mode === 'Cash' ? 'bg-green-100 text-green-800' :
                                  expense.payment_mode === 'Bank' ? 'bg-blue-100 text-blue-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {expense.payment_mode || 'N/A'}
                                </span>
                              </td>
                              <td className="p-2 text-right text-red-600 font-medium">₹{expense.amount?.toLocaleString('en-IN')}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="sticky bottom-0 bg-red-50">
                          <tr className="font-bold">
                            <td colSpan="3" className="p-2 text-right">Total:</td>
                            <td className="p-2 text-right text-red-700">₹{report.summary.total_expense.toLocaleString('en-IN')}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default IncomeExpensesReport;
